import { useMemo, useState } from "react";
import {
  useAddGameplanMember,
  useGameplanMembers,
  useRemoveGameplanMember,
  type CampaignMember,
} from "../hooks/useGameplans";
import { useWorkspace, type WorkspaceMembership } from "../hooks/useWorkspace";

interface Props {
  workspaceId: string;
  campaignId: string;
  canManage: boolean;
}

/**
 * Campaign membership management panel. Lists members + roles, lets a
 * CXO add a workspace member with a role pick and remove non-lead /
 * non-busy members. Surface the two guard errors (last_lead,
 * open_assignments) as inline messages rather than alert() so the
 * remove failure stays in context.
 */
export function GameplanMembersPanel({ workspaceId, campaignId, canManage }: Props) {
  const members = useGameplanMembers(workspaceId, campaignId);
  const workspace = useWorkspace(workspaceId);
  const add = useAddGameplanMember(workspaceId, campaignId);
  const remove = useRemoveGameplanMember(workspaceId, campaignId);
  const [pickedUserId, setPickedUserId] = useState("");
  const [pickedRole, setPickedRole] = useState<"member" | "lead">("member");
  const [removeError, setRemoveError] = useState<{
    userId: string;
    code: string;
  } | null>(null);

  // Build a lookup so the row labels show real names instead of ULIDs.
  const userMap = useMemo(() => {
    const m = new Map<string, WorkspaceMembership>();
    for (const u of workspace.data?.memberships ?? []) m.set(u.userId, u);
    return m;
  }, [workspace.data]);

  // Picker options: workspace members not already in the campaign.
  const memberIds = new Set((members.data?.items ?? []).map((m) => m.userId));
  const pickable = (workspace.data?.memberships ?? []).filter(
    (m) => !memberIds.has(m.userId) && m.acceptedAt !== null,
  );

  if (members.isLoading) return <div style={{ color: "var(--text-3)" }}>Loading members…</div>;
  if (members.error) {
    return (
      <div style={{ color: "var(--danger)" }}>Failed to load members.</div>
    );
  }

  const list = members.data?.items ?? [];

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
        {list.map((m) => (
          <MemberRow
            key={m.userId}
            member={m}
            label={labelFor(m, userMap.get(m.userId))}
            canManage={canManage}
            removing={remove.isPending && remove.variables === m.userId}
            errorCode={removeError?.userId === m.userId ? removeError.code : null}
            onRemove={() => {
              setRemoveError(null);
              remove.mutate(m.userId, {
                onError: (e: Error & { body?: { error?: string } }) => {
                  const code = e.body?.error ?? "unknown";
                  setRemoveError({ userId: m.userId, code });
                },
              });
            }}
          />
        ))}
      </ul>

      {canManage && (
        <div
          style={{
            display: "flex",
            gap: 8,
            alignItems: "center",
            borderTop: "1px solid var(--border-2)",
            paddingTop: 12,
          }}
        >
          <select
            value={pickedUserId}
            onChange={(e) => setPickedUserId(e.target.value)}
            disabled={pickable.length === 0}
            style={{ flex: 1 }}
          >
            <option value="">
              {pickable.length === 0
                ? "All workspace members already added"
                : "Select workspace member…"}
            </option>
            {pickable.map((u) => (
              <option key={u.userId} value={u.userId}>
                {u.displayName} ({u.email})
              </option>
            ))}
          </select>
          <select
            value={pickedRole}
            onChange={(e) => setPickedRole(e.target.value as "member" | "lead")}
          >
            <option value="member">member</option>
            <option value="lead">lead</option>
          </select>
          <button
            type="button"
            disabled={!pickedUserId || add.isPending}
            onClick={() => {
              add.mutate(
                { userId: pickedUserId, role: pickedRole },
                {
                  onSuccess: () => {
                    setPickedUserId("");
                    setPickedRole("member");
                  },
                },
              );
            }}
          >
            {add.isPending ? "Adding…" : "Add"}
          </button>
        </div>
      )}

      {add.error && (
        <div style={{ color: "var(--danger)", fontSize: 13 }}>
          {add.error.message}
        </div>
      )}
    </div>
  );
}

function labelFor(
  m: CampaignMember,
  ws: WorkspaceMembership | undefined,
): string {
  if (!ws) return m.userId; // fallback — user removed from workspace
  return `${ws.displayName} (${ws.email})`;
}

interface RowProps {
  member: CampaignMember;
  label: string;
  canManage: boolean;
  removing: boolean;
  errorCode: string | null;
  onRemove: () => void;
}

function MemberRow({ member, label, canManage, removing, errorCode, onRemove }: RowProps) {
  return (
    <li
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        padding: "8px 0",
        borderBottom: "1px solid var(--border-1)",
      }}
    >
      <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
        <span>{label}</span>
        <span style={{ color: "var(--text-3)", fontSize: 12 }}>
          {member.role}
          {errorCode && (
            <>
              {" — "}
              <span style={{ color: "var(--danger)" }}>{messageFor(errorCode)}</span>
            </>
          )}
        </span>
      </div>
      {canManage && (
        <button
          type="button"
          disabled={removing}
          onClick={() => {
            if (
              confirm(
                `Remove ${label} from this campaign? Their access to campaign plays will be revoked immediately.`,
              )
            ) {
              onRemove();
            }
          }}
        >
          {removing ? "Removing…" : "Remove"}
        </button>
      )}
    </li>
  );
}

function messageFor(code: string): string {
  if (code === "last_lead")
    return "Can't remove the only lead. Promote another member first.";
  if (code === "open_assignments")
    return "Has open assignments on campaign plays. Reassign first.";
  return `Remove failed: ${code}`;
}
